// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';
import * as copy from './copy';
import { EventLog } from './eventlog';
import { createDrawer, createModalHost, el, formatBytes, formatCount, refuseToBeFramed, renderCopy, toast } from './ui/shell';
import { buildNeedles, needlesForValue } from './scan/needles';
import { isBrotli, isTextAsset, prepareNeedles, scanFile, type NeedleSpec } from './scan/engine';
import { extensionOf } from './domain/smfind';
import { isOvercommon } from './domain/gate';
import { looksLikeZip, normalise, pickDirectory, readFileList, readZip, walkEntry } from './ingest';
import { buildCsv, buildFix, buildReport, buildSourceZip, FILENAMES, saveOne } from './artefacts';
import { dedupeTree } from './domain/srcpath';
import type { ErrorReply, InitMessage, ScanMessage, ScanReply } from './worker';
import type { Match, Note, RecoveredFile, Redacted, ScanResult } from './types';

refuseToBeFramed();

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const log = new EventLog($<HTMLOListElement>('drawer-list'));
const modal = createModalHost($('modal-host'));
createDrawer($('drawer'), $<HTMLButtonElement>('log-toggle'), $<HTMLButtonElement>('log-close'));

/* ── state ──────────────────────────────────────────────────────────────────────────────────── */

interface BuildInput {
  files: { path: string; bytes: Uint8Array }[];
  skippedPaths: { path: string; reason: string }[];
  brotli: string[];
  inflatedGzip: number;
}

let envFiles: { name: string; content: string }[] = [];
let build: BuildInput | null = null;
let result: ScanResult | null = null;
let recovered: RecoveredFile[] = [];
let cancelled = false;
let showAll = false;

/* ── static copy ────────────────────────────────────────────────────────────────────────────── */

$('brand-tagline').textContent = copy.TAGLINE;
$('hero-title').textContent = copy.HERO_TITLE;
$('hero-body').replaceChildren(renderCopy(copy.HERO_BODY));
$('trust-strong').textContent = copy.TRUST_BANNER_STRONG;
$('trust-body').textContent = ` ${copy.TRUST_BANNER_BODY}`;
$('env-hint').replaceChildren(renderCopy(copy.ENV_ZONE_HINT));
$('build-hint').replaceChildren(renderCopy(copy.BUILD_ZONE_HINT));
$('honesty').textContent = copy.LIMITS_HONESTY;
$('limits-list').replaceChildren(...copy.LIMITS.map((l) => el('li', {}, [renderCopy(l)])));

/* ── theme (the ONLY thing that persists) ───────────────────────────────────────────────────── */

const THEME_KEY = 'leakmap.theme';
try {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === 'light' || saved === 'dark') document.documentElement.dataset['theme'] = saved;
} catch {
  /* a locked-down browser is not an error */
}
$('theme-toggle').addEventListener('click', () => {
  const next = document.documentElement.dataset['theme'] === 'light' ? 'dark' : 'light';
  document.documentElement.dataset['theme'] = next;
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    /* as above */
  }
});

/* ── modals ─────────────────────────────────────────────────────────────────────────────────── */

function list(items: readonly string[]): HTMLUListElement {
  return el('ul', {}, items.map((i) => el('li', {}, [renderCopy(i)])));
}

function openModal(which: string): void {
  if (which === 'how') {
    const frag = document.createDocumentFragment();
    for (const s of copy.HOW_STEPS) {
      frag.append(el('div', { class: 'step' }, [el('h4', {}, [s.title]), el('p', {}, [renderCopy(s.body)])]));
    }
    frag.append(el('h3', {}, ['Keyboard']));
    const dl = el('dl', { class: 'keymap' });
    for (const k of copy.KEYBOARD) {
      dl.append(el('dt', {}, [k.keys]), el('dd', {}, [k.what]));
    }
    frag.append(dl);
    modal.show('How leakmap works', frag);
    return;
  }
  if (which === 'privacy') {
    const t = ($('modal-host').ownerDocument.getElementById('tpl-privacy') as HTMLTemplateElement).content.cloneNode(
      true,
    ) as DocumentFragment;
    t.getElementById('privacy-protected')!.replaceWith(list(copy.PRIVACY_PROTECTED));
    t.getElementById('privacy-not')!.replaceWith(list(copy.PRIVACY_NOT));
    t.getElementById('privacy-trust')!.replaceWith(list(copy.PRIVACY_TRUST));
    modal.show('Privacy & threat model', t);
    return;
  }
  if (which === 'limits') {
    const frag = document.createDocumentFragment();
    frag.append(el('p', { class: 'honesty' }, [copy.LIMITS_HONESTY]), list(copy.LIMITS));
    modal.show('What leakmap cannot tell you', frag);
    return;
  }
  if (which === 'about') {
    const t = (document.getElementById('tpl-about') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;
    t.getElementById('about-body')!.replaceChildren(renderCopy(copy.ABOUT));
    modal.show('About leakmap', t);
  }
}

for (const btn of document.querySelectorAll<HTMLButtonElement>('[data-modal]')) {
  btn.addEventListener('click', () => openModal(btn.dataset['modal']!));
}

/* ── glossary ───────────────────────────────────────────────────────────────────────────────── */

const tooltip = $('tooltip');
document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  if (t.classList?.contains('glossary-link')) {
    const def = copy.GLOSSARY[t.dataset['term'] ?? ''];
    if (!def) return;
    tooltip.textContent = def;
    tooltip.removeAttribute('hidden');
    const r = t.getBoundingClientRect();
    tooltip.style.left = `${Math.min(r.left, window.innerWidth - tooltip.offsetWidth - 12)}px`;
    tooltip.style.top = `${r.bottom + 6}px`;
    e.stopPropagation();
    return;
  }
  tooltip.setAttribute('hidden', '');
});

/* ── ingest: the .env ───────────────────────────────────────────────────────────────────────── */

const envPaste = $<HTMLTextAreaElement>('env-paste');
const envStatus = $('env-status');

function syncEnvFromPaste(): void {
  const text = envPaste.value;
  envFiles = text.trim() === '' ? [] : [{ name: '.env', content: text }];
  updateEnvStatus();
}

function updateEnvStatus(): void {
  if (envFiles.length === 0) {
    envStatus.textContent = '';
    updateRunState();
    return;
  }
  const build = buildNeedles(envFiles, 'production');
  const searched = new Set(build.specs.map((s) => s.envKey)).size;
  envStatus.textContent =
    `${formatCount(build.resolved.length)} variable(s) read from ${envFiles.map((f) => f.name).join(', ')} · ` +
    `${formatCount(searched)} will be searched for, ${formatCount(build.skipped.length)} skipped (listed after the run).`;
  updateRunState();
}

envPaste.addEventListener('input', syncEnvFromPaste);

$('pick-env').addEventListener('click', () => $<HTMLInputElement>('env-input').click());
$<HTMLInputElement>('env-input').addEventListener('change', async (e) => {
  const files = (e.target as HTMLInputElement).files;
  if (!files) return;
  await loadEnvFiles(Array.from(files));
});

async function loadEnvFiles(files: File[]): Promise<void> {
  const out: { name: string; content: string }[] = [];
  for (const f of files) out.push({ name: f.name, content: await f.text() });
  envFiles = out;
  // Show the highest-precedence file in the box so the user can see what leakmap read.
  envPaste.value = out.map((f) => `# ${f.name}\n${f.content}`).join('\n');
  log.emit('info', `Read ${out.length} .env file(s): ${out.map((f) => f.name).join(', ')}`);
  updateEnvStatus();
}

wireDropzone($('env-dropzone'), async (dt) => {
  const files = Array.from(dt.files ?? []);
  if (files.length) await loadEnvFiles(files);
});

/* ── ingest: the build ──────────────────────────────────────────────────────────────────────── */

const buildStatus = $('build-status');

function setBuild(raw: { path: string; bytes: Uint8Array }[]): void {
  const n = normalise(raw);
  build = { files: n.files, skippedPaths: n.skippedPaths, brotli: n.brotli, inflatedGzip: n.inflatedGzip };
  const bytes = n.files.reduce((a, f) => a + f.bytes.length, 0);
  const text = n.files.filter((f) => isTextAsset(f.path)).length;
  buildStatus.textContent =
    `${formatCount(n.files.length)} file(s), ${formatBytes(bytes)} — ${formatCount(text)} searchable as text` +
    (n.inflatedGzip ? `, ${n.inflatedGzip} gzip sibling(s) inflated` : '') +
    (n.brotli.length ? `. ${n.brotli.length} brotli asset(s) will NOT be scanned — the browser has no brotli decoder.` : '.');
  log.emit('good', `Build loaded: ${n.files.length} files, ${formatBytes(bytes)}`);
  if (n.skippedPaths.length) log.emit('warn', `${n.skippedPaths.length} path(s) skipped as unsafe or oversized`);
  updateRunState();
}

async function ingestDataTransfer(dt: DataTransfer): Promise<void> {
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items ?? [])) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  if (entries.length) {
    const raw: { path: string; bytes: Uint8Array }[] = [];
    for (const e of entries) raw.push(...(await walkEntry(e)));
    if (raw.length === 1 && looksLikeZip(raw[0]!.bytes)) {
      setBuild(await readZip(raw[0]!.bytes));
      return;
    }
    setBuild(raw);
    return;
  }
  const files = Array.from(dt.files ?? []);
  if (files.length === 1) {
    const bytes = new Uint8Array(await files[0]!.arrayBuffer());
    if (looksLikeZip(bytes)) {
      setBuild(await readZip(bytes));
      return;
    }
  }
  setBuild(await readFileList(dt.files!));
}

wireDropzone($('build-dropzone'), ingestDataTransfer);

$('build-dropzone').addEventListener('click', (e) => {
  if ((e.target as HTMLElement).closest('button')) return;
  $<HTMLInputElement>('files-input').click();
});

// The directory picker is Chromium-desktop only, so the button only appears where it works.
if (typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function') {
  const b = $<HTMLButtonElement>('pick-folder');
  b.hidden = false;
  b.addEventListener('click', async () => {
    try {
      const raw = await pickDirectory();
      if (raw) setBuild(raw);
    } catch (err) {
      if ((err as DOMException)?.name !== 'AbortError') showError('That folder could not be read.');
    }
  });
}

$('pick-files').addEventListener('click', () => $<HTMLInputElement>('files-input').click());
$<HTMLInputElement>('files-input').addEventListener('change', async (e) => {
  const files = (e.target as HTMLInputElement).files;
  if (!files || files.length === 0) return;
  if (files.length === 1) {
    const bytes = new Uint8Array(await files[0]!.arrayBuffer());
    if (looksLikeZip(bytes)) {
      setBuild(await readZip(bytes));
      return;
    }
  }
  setBuild(await readFileList(files));
});

function wireDropzone(zone: HTMLElement, handler: (dt: DataTransfer) => Promise<void>): void {
  for (const type of ['dragenter', 'dragover']) {
    zone.addEventListener(type, (e) => {
      e.preventDefault();
      zone.classList.add('is-over');
    });
  }
  for (const type of ['dragleave', 'drop']) {
    zone.addEventListener(type, () => zone.classList.remove('is-over'));
  }
  zone.addEventListener('drop', async (e) => {
    e.preventDefault();
    const dt = (e as DragEvent).dataTransfer;
    if (!dt) return;
    try {
      await handler(dt);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'That could not be read.');
    }
  });
}

/* ── the sample ─────────────────────────────────────────────────────────────────────────────── */

$('try-sample').addEventListener('click', async (e) => {
  // stopPropagation so the dropzone's own click handler does not also open the file picker.
  e.stopPropagation();
  try {
    log.emit('info', 'Loading the sample build…');
    const [envRes, zipRes] = await Promise.all([fetch('/samples/env.sample'), fetch('/samples/leaky-build.zip')]);
    if (!envRes.ok || !zipRes.ok) throw new Error('sample missing');
    envPaste.value = await envRes.text();
    syncEnvFromPaste();
    setBuild(await readZip(new Uint8Array(await zipRes.arrayBuffer())));
    toast('Sample loaded — press "Search my build".');
  } catch {
    showError('The sample could not be loaded.');
  }
});

/* ── the run ────────────────────────────────────────────────────────────────────────────────── */

function updateRunState(): void {
  const ready = build !== null && build.files.length > 0;
  $<HTMLButtonElement>('run').disabled = !ready;
  $('run-hint').textContent = ready
    ? envFiles.length === 0
      ? 'No .env given — leakmap will still look for well-known credential shapes and for a whole environment object compiled into your build.'
      : ''
    : 'Give leakmap a built folder to search.';
  $<HTMLButtonElement>('start-over').hidden = !ready && envFiles.length === 0;
}

$('run').addEventListener('click', () => void run());
document.addEventListener('keydown', (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
    e.preventDefault();
    if (!$<HTMLButtonElement>('run').disabled) void run();
  } else if (e.key === '?' && !(e.target as HTMLElement).matches('textarea,input')) {
    openModal('how');
  } else if ((e.key === 'l' || e.key === 'L') && !(e.target as HTMLElement).matches('textarea,input')) {
    $<HTMLButtonElement>('log-toggle').click();
  }
});

$('cancel').addEventListener('click', () => {
  cancelled = true;
  log.emit('warn', 'Cancelled by the user.');
});

$('start-over').addEventListener('click', () => {
  envFiles = [];
  build = null;
  result = null;
  recovered = [];
  envPaste.value = '';
  envStatus.textContent = '';
  buildStatus.textContent = '';
  for (const id of ['verdict-panel', 'notes-panel', 'matches-panel', 'skipped-panel', 'export-panel', 'error']) {
    $(id).hidden = true;
  }
  updateRunState();
  log.emit('info', 'Cleared.');
});

$('retry').addEventListener('click', () => {
  $('error').hidden = true;
});

function showError(message: string): void {
  $('error-text').textContent = message;
  $('error').hidden = false;
  log.emit('error', message);
}

const POOL_SIZE = Math.min(Math.max((navigator.hardwareConcurrency || 4) - 1, 1), 4);

async function run(): Promise<void> {
  if (!build) return;
  cancelled = false;
  $('error').hidden = true;
  $('progress').hidden = false;
  $<HTMLButtonElement>('run').disabled = true;

  const needleBuild = buildNeedles(envFiles, 'production');
  const specs: NeedleSpec[] = needleBuild.specs;
  log.emit(
    'info',
    `Searching for ${formatCount(specs.length)} pattern(s) built from ${formatCount(new Set(specs.map((s) => s.envKey)).size)} variable(s).`,
  );
  for (const bug of needleBuild.configBugs) log.emit('warn', `${bug.key}: expansion produced an empty string`);

  // Which .map paths are actually referenced — the orphan check needs the whole tree first.
  const referencedMaps = new Set<string>();
  for (const f of build.files) {
    const ext = extensionOf(f.path);
    if (ext === 'map') continue;
    if (!isTextAsset(f.path)) continue;
    // A cheap prefilter: only decode files that could carry a comment.
    if (ext !== 'js' && ext !== 'mjs' && ext !== 'css') continue;
    const tail = new TextDecoder().decode(f.bytes.subarray(Math.max(0, f.bytes.length - 4096)));
    const m = /sourceMappingURL=(\S+)/.exec(tail);
    if (!m || !m[1] || m[1].startsWith('data:')) continue;
    const dir = f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/') + 1) : '';
    referencedMaps.add(`${dir}${m[1]}`.replace(/^\.\//, ''));
  }

  const workers: Worker[] = [];
  const matches: Redacted<Match>[] = [];
  const notes: Note[] = [];
  const recoveredRaw: RecoveredFile[] = [];
  const hitCounts = new Map<number, number>();
  let scanned = 0;
  let bytesDone = 0;
  let skippedBinary = 0;
  const started = performance.now();
  const totalBytes = build.files.reduce((a, f) => a + f.bytes.length, 0);

  try {
    for (let i = 0; i < POOL_SIZE; i++) {
      const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
      workers.push(w);
      const init: InitMessage = {
        type: 'init',
        specs,
        referencedMaps: [...referencedMaps],
        hasEnv: envFiles.length > 0,
      };
      w.postMessage(init);
    }

    let next = 0;
    const queue = build.files;
    await Promise.all(
      workers.map(
        (w) =>
          new Promise<void>((resolve) => {
            const dispatch = () => {
              if (cancelled || next >= queue.length) {
                resolve();
                return;
              }
              const file = queue[next++]!;
              // The bytes are COPIED rather than transferred: the main thread still owns them, and
              // a second run over the same build must not find a detached buffer.
              const buf = file.bytes.slice().buffer;
              const msg: ScanMessage = { type: 'scan', id: next, path: file.path, bytes: buf };
              w.postMessage(msg, [buf]);
            };
            w.onmessage = (ev: MessageEvent<ScanReply | ErrorReply | { type: 'ready' }>) => {
              const d = ev.data;
              if (d.type === 'ready') {
                dispatch();
                return;
              }
              if (d.type === 'result') {
                matches.push(...d.matches);
                notes.push(...d.notes);
                recoveredRaw.push(...d.recovered);
                for (const [k, v] of d.hitCounts) hitCounts.set(k, (hitCounts.get(k) ?? 0) + v);
                if (d.isText) scanned++;
                else if (!isBrotli(d.path)) skippedBinary++;
                bytesDone += d.bytes;
              } else {
                log.emit('error', `${d.path}: ${d.errorClass}`);
              }
              const pct = totalBytes === 0 ? 100 : Math.round((bytesDone / totalBytes) * 100);
              ($('progress-fill') as HTMLElement).style.width = `${pct}%`;
              const mbps = bytesDone / 1048576 / ((performance.now() - started) / 1000);
              $('progress-label').textContent = `${pct}% · ${formatBytes(bytesDone)} of ${formatBytes(totalBytes)} · ${mbps.toFixed(1)} MB/s`;
              dispatch();
            };
            w.onerror = () => resolve();
          }),
      ),
    );
  } finally {
    for (const w of workers) w.terminate();
  }

  // Post-hoc rung 9: demote any needle that produced more hits than a signal ever could, WITH ITS
  // REAL COUNT — never a predicted one.
  const overcommon = new Set<string>();
  for (const [idx, count] of hitCounts) {
    if (!isOvercommon(count)) continue;
    const spec = specs[idx];
    if (spec) overcommon.add(spec.envKey);
  }
  const kept = matches.filter((m) => !overcommon.has(m.envKey));
  for (const key of overcommon) {
    const count = [...hitCounts.entries()]
      .filter(([i]) => specs[i]?.envKey === key)
      .reduce((a, [, c]) => a + c, 0);
    notes.push({
      id: 'overcommon',
      severity: 'info',
      title: `${key} matched too often to be a signal`,
      detail: `Its value appears ${formatCount(count)} times in your build, which means it is ordinary content rather than a credential leak. leakmap has set it aside rather than burying the real findings under it.`,
    });
  }

  recovered = dedupeTree(recoveredRaw.map((r) => ({ path: r.path, content: r.content }))).map((f) => ({
    ...f,
    fromMap: recoveredRaw.find((r) => r.content === f.content)?.fromMap ?? '',
  }));

  result = {
    matches: kept,
    notes: dedupeNotes(notes),
    recovered,
    assets: {
      scanned,
      bytes: bytesDone,
      skippedBinary,
      skippedBrotli: build.brotli,
      inflatedGzip: build.inflatedGzip,
    },
    needleCount: specs.length,
    skipped: needleBuild.skipped.map((s) => ({ key: s.key, reason: s.reason })),
  };

  $('progress').hidden = true;
  $<HTMLButtonElement>('run').disabled = false;
  log.emit('good', `Search complete: ${formatCount(kept.length)} occurrence(s) across ${formatCount(scanned)} file(s).`);
  render(needleBuild.formCount, needleBuild.skipped);
}

function dedupeNotes(notes: Note[]): Note[] {
  const seen = new Set<string>();
  const out: Note[] = [];
  for (const n of notes) {
    const k = `${n.id}|${n.path ?? ''}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(n);
  }
  return out;
}

/* ── render ─────────────────────────────────────────────────────────────────────────────────── */

const SEV_ORDER: Record<Note['severity'], number> = { critical: 0, high: 1, medium: 2, low: 3, expected: 4, info: 5 };

function render(formCount: number, skipped: { key: string; reason: string; secretShaped: boolean; value: string }[]): void {
  if (!result) return;
  const r = result;

  // ── verdict
  const panel = $('verdict-panel');
  const heading = $('verdict-heading');
  const body = $('verdict-body');
  const extra = $('verdict-extra');
  extra.replaceChildren();
  panel.hidden = false;

  const envKeys = new Set(r.matches.filter((m) => !m.ruleId).map((m) => m.envKey));
  const searchedValues = new Set(
    envFiles.length ? buildNeedles(envFiles, 'production').specs.map((s) => s.envKey) : [],
  ).size;

  if (envKeys.size === 0) {
    panel.classList.remove('has-findings');
    // Neutral grey, never a green tick: colour is a claim.
    extra.append(el('div', { class: 'verdict-badge' }, ['NO VERBATIM MATCH']));
    heading.textContent = copy.CLEAN_HEADLINE;
    body.textContent = copy.cleanBody(searchedValues, r.assets.scanned, Math.max(formCount, 1));
    const ul = el('ul', { class: 'caveats' });
    for (const c of copy.CLEAN_CAVEATS) {
      ul.append(el('li', {}, [el('strong', {}, [c.title]), c.body]));
    }
    extra.append(ul);
  } else {
    panel.classList.add('has-findings');
    heading.textContent = copy.findingsHeadline(envKeys.size, searchedValues);
    body.textContent = copy.FINDINGS_BODY;
  }

  const stats = [
    `${formatCount(searchedValues)} value(s) searched for`,
    `${formatCount(r.assets.scanned)} file(s) read as text`,
    formatBytes(r.assets.bytes),
    `${formatCount(r.matches.length)} occurrence(s)`,
  ];
  if (r.assets.skippedBinary) stats.push(`${formatCount(r.assets.skippedBinary)} non-text file(s) not searched`);
  if (r.assets.skippedBrotli.length) stats.push(`${r.assets.skippedBrotli.length} brotli asset(s) not scanned`);
  if (r.assets.inflatedGzip) stats.push(`${r.assets.inflatedGzip} gzip sibling(s) inflated first`);
  extra.append(el('p', { class: 'run-stats' }, [stats.join(' · ')]));
  heading.focus();

  // ── notes
  const notesPanel = $('notes-panel');
  const notesList = $('notes-list');
  const sorted = [...r.notes].sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity]);
  notesPanel.hidden = sorted.length === 0;
  notesList.replaceChildren(
    ...sorted.map((n) =>
      el('li', { class: `sev-${n.severity}` }, [
        el('h3', {}, [n.title]),
        el('p', {}, [n.detail, ...(n.path ? [' ', el('code', {}, [n.path])] : [])]),
      ]),
    ),
  );

  // ── matches
  renderMatches();

  // ── skipped
  const skippedPanel = $('skipped-panel');
  skippedPanel.hidden = skipped.length === 0;
  $('skipped-list').replaceChildren(
    ...skipped.map((s) => {
      const li = el('li', { class: s.secretShaped ? 'is-secret-shaped' : '' }, [
        el('span', { class: 'k' }, [s.key]),
        el('span', { class: 'why' }, [s.reason]),
      ]);
      const btn = el('button', { type: 'button', class: 'pill' }, ['Search anyway']);
      btn.addEventListener('click', () => void searchAnyway(s.key, s.value, btn));
      li.append(btn);
      return li;
    }),
  );

  // ── exports
  $('export-panel').hidden = false;
  $('export-note').textContent =
    'One file per click — a page that fires four downloads from one gesture trips the browser’s download limiter and the last three silently never arrive.';
  const srcBtn = $<HTMLButtonElement>('dl-source');
  srcBtn.hidden = r.recovered.length === 0;
  const warn = $('source-warning');
  warn.hidden = r.recovered.length === 0;
  if (r.recovered.length) {
    warn.textContent =
      `${formatCount(r.recovered.length)} source file(s) were recovered from the sourcemaps in this build. ` +
      'That archive is NOT masked and cannot be — it is a verbatim copy of what your own sourcemaps already publish, ' +
      'which is the finding. It may contain credentials that were never in your .env, and paths carrying your username.';
  }
}

function renderMatches(): void {
  if (!result) return;
  const rows = result.matches.filter((m) => showAll || m.ruleId !== 'stripe-publishable');
  const panel = $('matches-panel');
  panel.hidden = rows.length === 0;
  if (rows.length === 0) return;

  const table = $('matches-table');
  const head = el('tr', {}, [
    el('th', {}, ['Variable / rule']),
    el('th', {}, ['Value']),
    el('th', {}, ['Where']),
    el('th', {}, ['How it got there']),
    el('th', {}, ['Context']),
  ]);
  const body = el('tbody');
  for (const m of rows) {
    const where = el('td', {}, [el('div', { class: 'mono' }, [m.path])]);
    where.append(el('div', { class: 'loc' }, [locationLine(m)]));
    const how = el('td', {}, [howLine(m)]);
    if (m.variantId !== 'raw') how.append(el('div', { class: 'loc' }, [`matched as: ${m.variantId}`]));
    const ctx = el('td', { class: 'ctx' });
    if (m.beforeRedacted || m.afterRedacted) {
      ctx.append(m.beforeRedacted, el('b', {}, ['[value]']), m.afterRedacted);
    }
    body.append(el('tr', {}, [el('td', { class: 'mono' }, [m.envKey]), el('td', { class: 'masked' }, [m.masked]), where, how, ctx]));
  }
  table.replaceChildren(el('thead', {}, [head]), body);
}

$<HTMLSelectElement>('filter-kind').addEventListener('change', (e) => {
  showAll = (e.target as HTMLSelectElement).value === 'all';
  renderMatches();
});

function locationLine(m: Match): string {
  // The raw byte offset is ALWAYS printed beside the mapped location, so a bad mapping is visible
  // rather than confidently wrong.
  const base = `byte ${formatCount(m.byteOffset)}`;
  if (!m.location) return base;
  const at = `${base} · line ${m.location.generatedLine + 1}, col ${m.location.utf16Column}`;
  switch (m.location.outcome) {
    case 'mapped':
      return `${at} → ${m.location.source}:${m.location.originalLine}:${m.location.originalColumn}`;
    case 'approximate':
      return `${at} → near ${m.location.source}:${m.location.originalLine} (approximate)`;
    case 'unmapped':
      return `${at} → no original location (not covered by the sourcemap)`;
    case 'refused-index-map':
      return `${at} → original location refused (index map)`;
    default:
      return at;
  }
}

function howLine(m: Match): string {
  switch (m.kind) {
    case 'literal-in-source':
      return 'written literally in your source';
    case 'build-inlined':
      return 'substituted at build time — the mapped line is the expression that was replaced, not where your secret lives';
    default:
      return m.ruleId ? 'matched a known credential shape' : 'no sourcemap for this file';
  }
}

/** The "search anyway" override for a value the gate set aside. */
function searchAnyway(key: string, value: string, btn: HTMLElement): void {
  if (!build) return;
  btn.textContent = 'Searching…';
  const specs = needlesForValue(key, value);
  const prepared = prepareNeedles(specs);
  let hits = 0;
  for (const f of build.files) {
    if (!isTextAsset(f.path)) continue;
    hits += scanFile({ path: f.path, bytes: f.bytes }, prepared, { referencedMaps: new Set(), hasEnv: true }).matches.length;
  }
  btn.textContent = `${formatCount(hits)} match(es)`;
  log.emit(hits > 0 ? 'warn' : 'info', `${key}: forced search found ${hits} match(es).`);
  toast(
    hits === 0
      ? `${key} does not appear in your build.`
      : `${key} appears ${formatCount(hits)} time(s) — which is why leakmap does not search for values like this by default.`,
  );
}

/* ── exports ────────────────────────────────────────────────────────────────────────────────── */

$('dl-report').addEventListener('click', () => {
  if (!result) return;
  void saveOne(FILENAMES.report, buildReport(result, new Date().toISOString().slice(0, 16).replace('T', ' ')), 'text/html');
  log.emit('good', `Wrote ${FILENAMES.report}`);
});
$('dl-csv').addEventListener('click', () => {
  if (!result) return;
  void saveOne(FILENAMES.csv, buildCsv(result), 'text/csv');
  log.emit('good', `Wrote ${FILENAMES.csv}`);
});
$('dl-fix').addEventListener('click', () => {
  if (!result) return;
  void saveOne(FILENAMES.fix, buildFix(result), 'text/markdown');
  log.emit('good', `Wrote ${FILENAMES.fix}`);
});
$('dl-source').addEventListener('click', () => {
  if (!result || result.recovered.length === 0) return;
  try {
    void saveOne(FILENAMES.source, buildSourceZip(result.recovered), 'application/zip');
    log.emit('warn', `Wrote ${FILENAMES.source} — ${result.recovered.length} unmasked source file(s).`);
  } catch (e) {
    showError(e instanceof Error ? e.message : 'The archive could not be written.');
  }
});

$('log-copy').addEventListener('click', async () => {
  try {
    await navigator.clipboard.writeText(log.asText());
    toast('Event log copied.');
  } catch {
    toast('Your browser would not let leakmap write to the clipboard.');
  }
});

if (typeof navigator.share === 'function') {
  const share = $<HTMLButtonElement>('share');
  share.hidden = false;
  share.addEventListener('click', async () => {
    if (!result) return;
    const file = new File([buildReport(result, new Date().toISOString().slice(0, 16).replace('T', ' '))], FILENAMES.report, {
      type: 'text/html',
    });
    try {
      await navigator.share({ files: [file], title: 'leakmap exposure report' });
    } catch {
      /* a cancelled share is not an error */
    }
  });
}

updateRunState();
log.emit('info', 'leakmap ready. Nothing you drop here leaves this tab.');
